<script setup>
import ConnectorIcon from 'dashboard/components-next/agents/ConnectorIcon.vue';
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MercadoLivreConnectionFields from './MercadoLivreConnectionFields.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  connector: {
    type: Object,
    default: null,
  },
  connectorTypes: {
    type: Object,
    default: () => ({}),
  },
  selectedType: {
    type: String,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);
const { t } = useI18n();

const formData = ref({
  name: '',
  connector_type: '',
  api_url: '',
  api_token: '',
  api_user: '',
  api_password: '',
  is_active: true,
  config: {},
});

const isEditing = computed(() => !!props.connector);

const selectedTypeInfo = computed(() => {
  if (!formData.value.connector_type) return null;
  return props.connectorTypes[formData.value.connector_type];
});

const namePlaceholder = computed(() => {
  if (selectedTypeInfo.value?.name) {
    return `ex: ${selectedTypeInfo.value.name} - Produção`;
  }
  return t('AI_AGENTS.CONNECTORS.FORM.NAME_PLACEHOLDER');
});

const needsCredentials = computed(() => {
  // Some connectors use user+password instead of token
  return ['mk_auth', 'sgp', 'sienge'].includes(formData.value.connector_type);
});

const isValid = computed(() => {
  if (
    !formData.value.name ||
    !formData.value.connector_type ||
    !formData.value.api_url
  ) {
    return false;
  }
  if (formData.value.connector_type === 'mercado_livre')
    return !!formData.value.config.inbox_id;
  // Ao editar, não exige token/senha (mantém os valores atuais)
  if (isEditing.value) {
    return true;
  }
  // Ao criar, exige credenciais
  if (needsCredentials.value) {
    return formData.value.api_user && formData.value.api_password;
  }
  return formData.value.api_token;
});

const handleSubmit = () => {
  if (!isValid.value) return;

  // Preparar dados, removendo campos vazios de credenciais ao editar
  const data = { ...formData.value };

  // Não enviar token/senha vazios (para manter os valores atuais)
  if (!data.api_token) delete data.api_token;
  if (!data.api_password) delete data.api_password;
  if (!data.api_user) delete data.api_user;

  emit('save', data);
};

watch(
  [() => props.connector, () => props.selectedType],
  ([newConnector, newType]) => {
    if (newConnector) {
      formData.value = {
        name: newConnector.name || '',
        connector_type: newConnector.connector_type || '',
        api_url: newConnector.api_url || '',
        api_token: '',
        api_user: newConnector.api_user || '',
        api_password: '',
        is_active: newConnector.is_active ?? true,
        config: { ...(newConnector.config || {}) },
      };
    } else {
      formData.value = {
        name: '',
        connector_type: newType || '',
        api_url:
          {
            tecimob: 'https://api.tecimob.com.br/v1/api',
            loja_integrada: 'https://api.awsli.com.br/v1',
            mercado_livre: 'https://api.mercadolibre.com',
          }[newType] || '',
        api_token: '',
        api_user: '',
        api_password: '',
        is_active: true,
        config: {},
      };
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="w-full">
    <form class="space-y-5" @submit.prevent="handleSubmit">
      <!-- Selected Type Header (when type is pre-selected) -->
      <div
        v-if="selectedTypeInfo"
        class="flex items-center gap-3 p-4 bg-n-alpha-2 rounded-xl border border-n-weak"
      >
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-11"
        >
          <ConnectorIcon :type="formData.connector_type" />
        </div>
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ selectedTypeInfo.name }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ selectedTypeInfo.description }}
          </p>
        </div>
      </div>

      <!-- Name -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('AI_AGENTS.CONNECTORS.FORM.NAME') }}
        </label>
        <input
          v-model="formData.name"
          type="text"
          class="w-full px-3 py-2.5 border border-n-weak rounded-xl bg-n-solid-1 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 read-only:bg-n-alpha-2"
          :placeholder="namePlaceholder"
        />
      </div>

      <!-- API URL -->
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('AI_AGENTS.CONNECTORS.FORM.API_URL') }}
        </label>
        <input
          v-model="formData.api_url"
          type="url"
          :readonly="
            ['tecimob', 'loja_integrada', 'mercado_livre'].includes(
              formData.connector_type
            )
          "
          class="w-full px-3 py-2.5 border border-n-weak rounded-xl bg-n-solid-1 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 read-only:bg-n-alpha-2"
          :placeholder="t('AI_AGENTS.CONNECTORS.FORM.API_URL_PLACEHOLDER')"
        />
      </div>

      <!-- Token (for token-based auth) -->
      <div v-if="formData.connector_type === 'sienge'" class="space-y-3">
        <p class="text-sm text-n-slate-11">{{ t('SIENGE.URL_HELP') }}</p>
        <label class="block text-sm text-n-slate-12">
          {{ t('SIENGE.DAILY_LIMIT') }}
          <input
            v-model.number="formData.config.daily_limit"
            type="number"
            min="1"
            max="200000"
            :placeholder="String(100)"
            class="mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2"
          />
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input v-model="formData.config.allow_writes" type="checkbox" />
          {{ t('SIENGE.ALLOW_WRITES') }}
        </label>
      </div>
      <MercadoLivreConnectionFields
        v-if="formData.connector_type === 'mercado_livre'"
        :config="formData.config"
        @update="formData.config = $event"
      />
      <div v-else-if="!needsCredentials">
        <label class="block text-sm font-medium text-n-slate-12 mb-1">
          {{ t('AI_AGENTS.CONNECTORS.FORM.TOKEN') }}
        </label>
        <input
          v-model="formData.api_token"
          type="password"
          class="w-full px-3 py-2.5 border border-n-weak rounded-xl bg-n-solid-1 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 read-only:bg-n-alpha-2"
          :placeholder="
            isEditing
              ? t('AI_AGENTS.CONNECTORS.FORM.TOKEN_PLACEHOLDER_EDIT')
              : t('AI_AGENTS.CONNECTORS.FORM.TOKEN_PLACEHOLDER')
          "
        />
        <p v-if="isEditing" class="mt-1 text-xs text-n-slate-11">
          {{ t('AI_AGENTS.CONNECTORS.FORM.TOKEN_HINT') }}
        </p>
      </div>

      <!-- User & Password (for credential-based auth) -->
      <template v-else>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('AI_AGENTS.CONNECTORS.FORM.USER') }}
          </label>
          <input
            v-model="formData.api_user"
            type="text"
            class="w-full px-3 py-2.5 border border-n-weak rounded-xl bg-n-solid-1 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 read-only:bg-n-alpha-2"
            :placeholder="t('AI_AGENTS.CONNECTORS.FORM.USER_PLACEHOLDER')"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1">
            {{ t('AI_AGENTS.CONNECTORS.FORM.PASSWORD') }}
          </label>
          <input
            v-model="formData.api_password"
            type="password"
            class="w-full px-3 py-2.5 border border-n-weak rounded-xl bg-n-solid-1 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 read-only:bg-n-alpha-2"
            :placeholder="
              isEditing
                ? t('AI_AGENTS.CONNECTORS.FORM.PASSWORD_PLACEHOLDER_EDIT')
                : t('AI_AGENTS.CONNECTORS.FORM.PASSWORD_PLACEHOLDER')
            "
          />
        </div>
      </template>

      <!-- Available Tools Info (compact) -->
      <div
        v-if="selectedTypeInfo?.tools?.length"
        class="flex flex-wrap items-center gap-1.5"
      >
        <span class="text-xs text-n-slate-11">{{
          t('AI_AGENTS.CONNECTORS.TOOLS_LABEL')
        }}</span>
        <span
          v-for="tool in selectedTypeInfo.tools"
          :key="tool"
          class="px-2.5 py-1 text-xs bg-n-alpha-2 text-n-slate-11 border border-n-weak rounded-lg"
        >
          {{ tool.replace(/_/g, ' ') }}
        </span>
      </div>

      <!-- Active -->
      <div
        class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3"
      >
        <input
          id="is_active"
          v-model="formData.is_active"
          type="checkbox"
          class="w-4 h-4 rounded border-n-weak bg-n-solid-1 text-n-brand focus:ring-n-brand"
        />
        <label for="is_active" class="cursor-pointer text-sm text-n-slate-12">
          {{ t('AI_AGENTS.CONNECTORS.FORM.ACTIVE') }}
        </label>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button
          :label="t('AI_AGENTS.CONNECTORS.FORM.CANCEL')"
          variant="faded"
          color="slate"
          @click="emit('cancel')"
        />
        <Button
          :label="
            isEditing
              ? t('AI_AGENTS.CONNECTORS.FORM.UPDATE')
              : t('AI_AGENTS.CONNECTORS.FORM.CREATE')
          "
          :is-loading="isSaving"
          :disabled="!isValid"
          @click="handleSubmit"
        />
      </div>
    </form>
  </div>
</template>
